import {
  normalizeMasterEntry,
  type AdminMasterEntry,
  type MasterEntry,
  type MasterKind,
  type MastersGateway,
  type SaveMasterEntryInput,
} from './types';

interface Row { id: string; code: string; name: string; active: boolean; sortOrder: number; updatedAt: string; }

const SEEDS: Readonly<Record<MasterKind, readonly { code: string; name: string; sortOrder: number }[]>> = {
  'cost-center': [
    { code: '10', name: 'ET-Teile Verkauf', sortOrder: 10 },
    { code: '20', name: 'Werkstatt', sortOrder: 20 },
    { code: '30', name: 'Service', sortOrder: 30 },
    { code: '40', name: 'Maschinenhandel', sortOrder: 40 },
    { code: '50', name: 'Vermietung', sortOrder: 50 },
    { code: '60', name: 'Garantie', sortOrder: 60 },
    { code: '70', name: 'Fuhrpark intern', sortOrder: 70 },
    { code: '80', name: 'Schrott', sortOrder: 80 },
    { code: '90', name: 'Spezialtransporte', sortOrder: 90 },
  ],
  warengruppe: [
    { code: 'MASCH', name: 'Maschinen', sortOrder: 10 },
    { code: 'ETEIL', name: 'Ersatzteile', sortOrder: 20 },
    { code: 'DL', name: 'Dienstleistung', sortOrder: 30 },
    { code: 'MIETE', name: 'Miete', sortOrder: 40 },
    { code: 'SONST', name: 'Sonstiges', sortOrder: 50 },
  ],
};

/**
 * Warengruppen-Name aus einer Demo-Id (`${tenantId}-warengruppe-${code}`) auflösen. Nur fürs Demo:
 * Stammdaten-/Rechnungs-Records tragen im Demo nur die Id; Supabase denormalisiert den Namen per JOIN.
 * So zeigt „Umsatz nach Warengruppe" auch ohne Server den echten Namen statt der groben Positionsart.
 */
const DEMO_WARENGRUPPE_NAMES: Readonly<Record<string, string>> = {
  MASCH: 'Maschinen', ETEIL: 'Ersatzteile', DL: 'Dienstleistung', MIETE: 'Miete', SONST: 'Sonstiges',
};
export function demoWarengruppeName(warengruppeId: string | null | undefined): string {
  if (!warengruppeId) return '';
  const marker = '-warengruppe-';
  const at = warengruppeId.lastIndexOf(marker);
  const code = at >= 0 ? warengruppeId.slice(at + marker.length) : null;
  return code ? (DEMO_WARENGRUPPE_NAMES[code] ?? code) : '';
}

/** In-Memory-Stämme je Mandant, mit denselben Defaults wie die Migration -- Demo == Supabase. */
export class DemoMastersGateway implements MastersGateway {
  private readonly store: Record<MasterKind, Map<string, Row[]>> = { 'cost-center': new Map(), warengruppe: new Map() };

  private rows(kind: MasterKind, tenantId: string): Row[] {
    let rows = this.store[kind].get(tenantId);
    if (!rows) {
      rows = SEEDS[kind].map((seed) => ({ id: `${tenantId}-${kind}-${seed.code}`, code: seed.code, name: seed.name, active: true, sortOrder: seed.sortOrder, updatedAt: '2026-07-25T00:00:00Z' }));
      this.store[kind].set(tenantId, rows);
    }
    return rows;
  }
  private sorted(rows: readonly Row[]): Row[] {
    return [...rows].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name));
  }
  private list(kind: MasterKind, tenantId: string): readonly MasterEntry[] {
    return this.sorted(this.rows(kind, tenantId).filter((row) => row.active))
      .map((row) => ({ id: row.id, code: row.code, name: row.name, active: row.active, sortOrder: row.sortOrder }));
  }
  private adminList(kind: MasterKind, tenantId: string): readonly AdminMasterEntry[] {
    return this.sorted(this.rows(kind, tenantId))
      .map((row) => ({ id: row.id, code: row.code, name: row.name, active: row.active, sortOrder: row.sortOrder, updatedAt: row.updatedAt, updatedByName: 'Demo' }));
  }
  private save(kind: MasterKind, input: SaveMasterEntryInput): void {
    const value = normalizeMasterEntry(input);
    const rows = this.rows(kind, value.tenantId);
    if (value.id) {
      const row = rows.find((entry) => entry.id === value.id);
      if (!row) throw new Error('Der Eintrag wurde nicht gefunden.');
      row.code = value.code; row.name = value.name; row.active = value.active; row.sortOrder = value.sortOrder;
      row.updatedAt = new Date().toISOString();
    } else {
      if (rows.some((entry) => entry.code.toLowerCase() === value.code.toLowerCase())) throw new Error('Dieser Code ist bereits vergeben.');
      rows.push({ id: `${value.tenantId}-${kind}-${value.code}-${rows.length}`, code: value.code, name: value.name, active: value.active, sortOrder: value.sortOrder, updatedAt: new Date().toISOString() });
    }
  }
  private remove(kind: MasterKind, tenantId: string, id: string): void {
    const rows = this.rows(kind, tenantId);
    const index = rows.findIndex((row) => row.id === id);
    if (index >= 0) rows.splice(index, 1);
  }

  async listCostCenters(tenantId: string) { return this.list('cost-center', tenantId); }
  async listWarengruppen(tenantId: string) { return this.list('warengruppe', tenantId); }
  async adminListCostCenters(tenantId: string) { return this.adminList('cost-center', tenantId); }
  async adminListWarengruppen(tenantId: string) { return this.adminList('warengruppe', tenantId); }
  async saveCostCenter(input: SaveMasterEntryInput) { this.save('cost-center', input); }
  async saveWarengruppe(input: SaveMasterEntryInput) { this.save('warengruppe', input); }
  async deleteCostCenter(tenantId: string, id: string) { this.remove('cost-center', tenantId, id); }
  async deleteWarengruppe(tenantId: string, id: string) { this.remove('warengruppe', tenantId, id); }
}

export function createDemoMastersGateway(): MastersGateway {
  return new DemoMastersGateway();
}
